import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { scrollIntoView } from '@theme/scrolling';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Sync the gallery's initial displayed media to the canonical selected variant on first
    // render, not just on later switches. Confirmed live: server-rendered sorted_media (native
    // Shopify Admin media order, see #syncMediaToVariant below) has no variant-awareness at all,
    // so the gallery's very first slide/grid item is whatever happens to sit first in that native
    // order - completely independent of which variant Shopify actually resolved as
    // selected_or_first_available_variant. Meanwhile the variant-picker's own server-rendered
    // <select> (selected="selected" baked into the HTML) and its embedded JSON script already
    // correctly reflect that resolved variant, including honoring a ?variant= URL param - verified
    // directly against raw server HTML and with a real ?variant= link. So the picker/variant-id
    // side was never the bug; only the gallery's initial media needs to catch up to it, using the
    // exact same sync mechanism (#syncMediaToVariant) as a real switch - reused, not duplicated.
    //
    // Owner correction (2026-08-31): scroll: false, not scrollBehavior: 'instant' as before - an
    // 'instant' scrollIntoView still forcibly repositions the viewport the instant this callback
    // runs, and connectedCallback isn't guaranteed to run before the user has already started
    // scrolling (custom-element upgrade timing, not something this file controls) - confirmed live
    // as the source of an initial-load bug where scrolling down right after navigation got yanked
    // back toward the top. Root cause, not a timing patch: this call was never actually about
    // moving the viewport in the first place, only about which media is marked "current" (the
    // carousel's slideshow.select() below, contained within the gallery, not a page scroll) and,
    // in grid mode, which item a LATER real variant switch should scroll to - on first paint,
    // scrolling the page to the initial variant's own image is never correct regardless of race
    // timing, since the visitor hasn't asked to see anything but the top of the page yet. Real
    // user-triggered switches (#handleProductSelect below) are untouched, still scroll (default
    // scroll: true) exactly as before.
    const initialVariantScript = target?.querySelector('variant-picker script[type="application/json"]');
    if (initialVariantScript) {
      try {
        const initialVariant = JSON.parse(initialVariantScript.textContent);
        this.#syncMediaToVariant(initialVariant?.featured_media?.id, {
          scroll: false,
          variantContext: initialVariant,
        });
      } catch {
        // Malformed/missing initial variant JSON - leave the native server-rendered order as-is.
      }
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // assets/variant-picker.js's own synchronous event detail (not the deferred promise result
    // read below) - false for a non-user-initiated section update, which must never scroll the
    // page. Defaults true, matching every real user-triggered switch.
    const scroll = event.detail?.scrollGallery !== false;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, resource } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);

        if (newMediaGallery instanceof MediaGallery) {
          newMediaGallery.#syncMediaToVariant(resource?.featured_media?.id, { scroll, variantContext: resource });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Restores the variant -> gallery media sync. snippets/product-media-gallery-content.liquid
   * (commit 0b90bb0) stopped moving the selected variant's featured_media to the front of
   * sorted_media, on purpose, so the first PDP image always matches the product card thumbnail
   * (native Shopify Admin order) - but that reorder was ALSO the only thing that ever made the
   * gallery display the right slide/media for a given variant, on a switch or on first render
   * alike: the commit's own claim that "the JS reacting to variant-change is unaffected" assumed a
   * mechanism that never actually existed. Reconnected here keyed by the variant's actual Shopify
   * media ID matched against each item's own data-media-id (snippets/product-media.liquid) - no
   * reordering, no title/color parsing, no second state store. Called both after a live variant
   * switch (#handleProductSelect above) and once from connectedCallback for the page's initial
   * variant, so there's exactly one sync mechanism instead of two.
   *
   * Two presentations need two different mechanisms, both native/existing (confirmed live,
   * media_presentation: grid, the default for this theme's product template): "carousel" mode
   * renders only the slideshow, always visible - handled via the same slideshow.select() API
   * #handleZoomMediaSelected already calls, which already no-ops when the requested slide is
   * already current (assets/slideshow.js), so a variant with no distinct media never causes an
   * unwanted reset. "grid" mode additionally renders slideshow-component (kept mobile-only,
   * display:none from 750px up) alongside a separate always-in-DOM .media-gallery__grid list that
   * becomes the visible one on desktop (see product-media-gallery-content-styles.liquid) - a plain
   * stacked list with no "current slide" concept at all, so there's nothing for slideshow.select()
   * to change there. Reused the same scrollIntoView helper slideshow.js/zoom-dialog.js already use
   * for the equivalent "bring this specific media into view" action, applied to the matching
   * .media-gallery__grid item instead of a slide.
   *
   * @param {number|string|undefined} featuredMediaId - The Shopify media ID assigned to the
   *   variant to sync to.
   * @param {object} [options]
   * @param {boolean} [options.scroll=true] - Whether to scroll the grid-mode item into view.
   *   false for the page's initial-load sync (connectedCallback above) - scrolling the viewport
   *   on first paint, before the visitor has done anything, is never correct regardless of
   *   timing; a real variant switch (#handleProductSelect above) always leaves this true.
   * @param {object} [options.variantContext] - The resolved variant object (or null), used only
   *   to name the product/variant in the console.warn below when featuredMediaId is missing.
   */
  #syncMediaToVariant(featuredMediaId, { scroll = true, variantContext } = {}) {
    // Pre-launch audit fix (CRITICAL - confirmed root cause of "selecting one color shows
    // another color's image"): Shopify does NOT auto-fill featured_media when no image is
    // explicitly assigned to a variant in Admin - it's genuinely null. Returning here (the
    // previous behavior) left whichever slide/scroll position the PREVIOUS variant selection had
    // on screen - i.e. exactly "color B shows color A's image," triggered by a missing Admin
    // association, not by media reordering (this sync is already keyed entirely by Shopify's own
    // stable media.id, never by position - reordering media in Admin does not affect it).
    // Per this task's own Phase 2 requirement: never invent a color-specific guess, but also
    // never silently leave a stale/wrong selection on screen - fall back to the gallery's own
    // first media item (Shopify's "default" convention, not a color-specific guess) and report
    // the missing Admin data so it can be fixed at the source.
    if (featuredMediaId == null) {
      this.#warnMissingFeaturedMedia(variantContext);
      featuredMediaId = this.#firstMediaId;
      if (featuredMediaId == null) return;
    }

    const { slideshow } = this;
    const slides = slideshow?.refs.slides;

    if (slides) {
      const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${featuredMediaId}"]`));
      if (index !== -1) slideshow?.select(index, undefined, { animate: false });
    }

    if (!scroll) return;

    const gridItem = this.media?.find((item) => item.querySelector(`[data-media-id="${featuredMediaId}"]`));
    if (gridItem) scrollIntoView(gridItem, { block: 'start', behavior: 'smooth' });
  }

  /**
   * The first media item's Shopify media ID, used as the safe fallback target when a variant has
   * no featured_media assigned in Admin. Checked in both possible gallery presentations - grid
   * mode's own ref="media[]" list (snippets/product-media-gallery-content.liquid), falling back
   * to the slideshow's first slide for carousel mode, where the grid ref array is never rendered.
   * @returns {string | null}
   */
  get #firstMediaId() {
    const gridFirst = this.media?.[0]?.querySelector('[data-media-id]');
    if (gridFirst) return gridFirst.getAttribute('data-media-id');

    const slideFirst = this.slideshow?.refs.slides?.[0]?.querySelector('[data-media-id]');
    return slideFirst?.getAttribute('data-media-id') ?? null;
  }

  /**
   * Reports a variant with no featured_media assigned in Shopify Admin - the actual Admin data
   * gap this falls back for, named explicitly so it can be fixed at the source rather than
   * rediscovered later as another "wrong color image" report.
   * @param {object} [variantContext] - The resolved variant object, if available.
   */
  #warnMissingFeaturedMedia(variantContext) {
    // product_id is not present on this serialized Variant object (confirmed live - the JSON
    // script only carries id/title/options/price/etc, see variant-picker-mockup.liquid's
    // `product.selected_or_first_available_variant | json`), so the page URL is the most reliable
    // product identifier actually available here.
    const variantId = variantContext?.id ?? 'unknown variant';
    const variantTitle = variantContext?.title ? ` ("${variantContext.title}")` : '';
    console.warn(
      `[media-gallery] Variant ${variantId}${variantTitle} on ${window.location.pathname} has no ` +
        'featured_media assigned in Shopify Admin. Falling back to the first product image instead of ' +
        'leaving a stale previous selection visible - assign an image to this variant in Admin to fix the ' +
        'gallery mapping.'
    );
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
